import { useCallback, useContext, useEffect, useState } from 'react';

import { ConfigContext } from 'Context/config';
import { usePhotosContext } from 'Context/photos';
import { UserContext } from 'Context/user';
import { VehicleLookupContext } from 'Context/vehicleLookup';

import { PHOTOS_PAGE_EVENTS } from 'Utilities/analytics/analyticsPhotosPage';
import { isMobileDevice } from 'Utilities/devices';
import { PARAMETER_MOTORWAY_PAGE } from 'Utilities/redirectToPhotoApp';

import { sendSms as sendSmsApi } from '../../../../services/api';
import { IMAGE_COUNT_MINIMUM } from '../VehicleProfiling.constants';
import { SKIPPED_PHOTOS } from '../VehicleProfiling.helpers';
import { useIsProfileConfirmed, useProfilingLocalStorage } from '../VehicleProfiling.hooks';

import { compareTimeDifference, RESEND_SMS_TIME, setShortUrl, setShortUrlsUTM } from './Photos.helpers';

export const useMandatoryPhotos = () => {
  const { isLoading: areMandatoryPhotosLoading, photos } = usePhotosContext();

  const hasAllMandatoryPhotos = photos?.filter(({ kind }) =>
    !SKIPPED_PHOTOS.includes(kind))?.length >= IMAGE_COUNT_MINIMUM;

  return { areMandatoryPhotosLoading, hasAllMandatoryPhotos };
};

type usePhotosAppUrlProps = {
  id?: number;
  queryExtension?: string;
  shouldFetch?: boolean;
};

export const usePhotosAppUrl = ({ id, queryExtension = '', shouldFetch = true }: usePhotosAppUrlProps): [boolean, string] => {
  const { vehicleLookupState: vehicleLookup } = useContext(VehicleLookupContext);
  const { userState: user } = useContext(UserContext);
  const { configState: { appClipUrl, photosAppUrl: photoWebAppUrl } } = useContext(ConfigContext);
  const loginToken = user?.login_token ?? '';
  const valuationRefQuery = `&valuation_ref=${vehicleLookup?.enquiry?.dpEnquiryId}`;

  /**
   * Prevent users from using the photos app
   * (if only `queryExtension` includes the `motorway_page` parameter until the
   * `motorway_page` parameter is handled on ios & android.
   * This allows the users to be redirected from the photos web
   * app back to the page that was specified in the parameter.
   */
  const photosUrl = queryExtension.includes(PARAMETER_MOTORWAY_PAGE) ? photoWebAppUrl : appClipUrl;

  const appClipLink = `${photosUrl}/${vehicleLookup.vrm ?? ''}?token=${loginToken}&${setShortUrlsUTM()?.toString()}${valuationRefQuery}${queryExtension}`;

  const [photosAppUrl, setPhotosAppUrl] = useState(() => appClipLink);
  const [isPhotosAppUrlLoading, setIsPhotosAppUrlLoading] = useState(shouldFetch);

  useEffect(() => {
    if (!shouldFetch) {
      setIsPhotosAppUrlLoading(false);
      return () => {};
    }

    const abortController = new AbortController();
    const controllerSignal = abortController.signal;

    (async () => {
      try {
        if (!id) {
          window?.Sentry?.captureException(new Error('id is absent'), {
            tags: {
              fetch: 'absent id for postShortUrl request',
            },
          });

          setIsPhotosAppUrlLoading(false);
          return;
        }

        setIsPhotosAppUrlLoading(true);

        const campaign = isMobileDevice({}) ? 'mobile' : 'desktop';

        const url = await setShortUrl({
          abortController,
          id,
          loginToken,
          photosQuery: `${setShortUrlsUTM(campaign)?.toString()}${valuationRefQuery}${queryExtension}`,
          photosUrl,
          vehicleVrm: vehicleLookup.vrm ?? '',
        });

        if (!controllerSignal.aborted) {
          setPhotosAppUrl(url);
        }
      } catch (err) {
        window?.Sentry?.captureException?.(err);
      } finally {
        if (!controllerSignal.aborted) {
          setIsPhotosAppUrlLoading(false);
        }
      }
    })();

    return () => {
      abortController?.abort();
      setIsPhotosAppUrlLoading(false);
    };
  }, [vehicleLookup.vrm, queryExtension, id, valuationRefQuery,
    photosUrl, loginToken, shouldFetch]);

  return [isPhotosAppUrlLoading, photosAppUrl];
};

export const PHOTOS_APP_SMS_SENT = 'photoAppSmsSent';
export const PHOTOS_APP_SMS_SENT_AT = 'photoAppSmsSentAt';

export const useSendSMS = () => {
  const { getItem, setItem } = useProfilingLocalStorage();
  const photoAppSmsSentAt = getItem(PHOTOS_APP_SMS_SENT_AT);

  const initialVal = photoAppSmsSentAt
    ? compareTimeDifference(new Date().getTime(), photoAppSmsSentAt, RESEND_SMS_TIME)
    : true;

  const [shouldResendSms, setShouldResendSms] = useState(initialVal);
  const { vehicleLookupState: vehicleLookup } = useContext(VehicleLookupContext);
  const { userState: user } = useContext(UserContext);
  const isProfileConfirmed = useIsProfileConfirmed();

  const enquiryId = vehicleLookup?.enquiry?.id;
  const sendSms = useCallback(() => sendSmsApi({ enquiryId }, user).then(() => {
    if (enquiryId) {
      PHOTOS_PAGE_EVENTS.PHOTOS_SMS_SEND({ enquiryId, isApiSuccess: true });
    }
  }), [enquiryId]);

  const sendSmsWithTimeStampChecking = useCallback(async () => {
    const currentTime = new Date().getTime();

    if (!isMobileDevice({ includeTablet: false }) && shouldResendSms && !isProfileConfirmed) {
      try {
        await sendSms();
        setItem(PHOTOS_APP_SMS_SENT_AT, currentTime);
        setShouldResendSms(false);
      } catch (err) {
        window?.Sentry?.captureException?.(err);
      }
    }
  }, [shouldResendSms]);

  const sendSmsWithFlagChecking = useCallback(async () => {
    const photoAppSmsSent = getItem(PHOTOS_APP_SMS_SENT);

    if (photoAppSmsSent) {
      return;
    }

    const currentTime = new Date().getTime();

    try {
      await sendSms();
      setItem(PHOTOS_APP_SMS_SENT, true);
      setItem(PHOTOS_APP_SMS_SENT_AT, currentTime);
      setShouldResendSms(false);
    } catch (err) {
      window?.Sentry?.captureException?.(err);
    }
  }, []);

  return { sendSms, sendSmsWithFlagChecking, sendSmsWithTimeStampChecking, shouldResendSms };
};
